import { useQuery } from '@tanstack/react-query';
import { useState, useEffect } from 'react';

function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

const SUPABASE_URL = 'https://dyftqspuiyabqzjslwyw.supabase.co';

// Generate a better placeholder logo with company initials
function generatePlaceholderLogo(supplierName: string, shortName?: string): string {
  const initials = shortName?.substring(0, 2) || supplierName?.substring(0, 2) || 'SU';
  const colors = ['3b82f6', '10b981', 'f59e0b', 'ef4444', '8b5cf6', '06b6d4', '84cc16', 'f97316'];
  const colorIndex = supplierName.length % colors.length;
  const color = colors[colorIndex];
  
  return `https://via.placeholder.com/40x40/${color}/ffffff?text=${initials.toUpperCase()}`;
}

export function useSupplierSearch(query: string) {
  // Load all suppliers initially for client-side filtering
  return useQuery({
    queryKey: ['collector-suppliers', 'all'],
    queryFn: async () => {
      console.log('[Supplier Search] Loading all suppliers for client-side filtering');

      // Try the UAT API directly first, fallback to mock data if it fails
      try {
        const uatUrl = `https://uat-api.billportal.io/api/v1/Supplier/GetCollectorSuppliers`;
        console.log('[Supplier Search] Trying UAT API directly:', uatUrl);
        
        const response = await fetch(uatUrl, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const suppliers = await response.json();
          console.log('[Supplier Search] UAT API success, found', suppliers.length, 'suppliers');
          console.log('[Supplier Search] First few suppliers:', suppliers.slice(0, 3));
          
          // Transform the UAT API response to match our expected format
          const transformedSuppliers = suppliers.map((supplier: any, index: number) => {
            // Check if logoUrl is a local file path (starts with C:/ or similar)
            const isLocalPath = supplier.logoUrl && (
              supplier.logoUrl.startsWith('C:/') || 
              supplier.logoUrl.startsWith('/') ||
              supplier.logoUrl.startsWith('\\')
            );
            
            // Use placeholder if no logo or if it's a local path
            const logoUrl = (!supplier.logoUrl || isLocalPath) 
              ? generatePlaceholderLogo(supplier.supplierName, supplier.supplierShortName)
              : supplier.logoUrl;
            
            return {
              id: `supplier-${index + 1}`,
              name: supplier.supplierName,
              supplierName: supplier.supplierName,
              shortName: supplier.supplierShortName,
              logo: logoUrl
            };
          });

          console.log('[Supplier Search] Transformed suppliers:', transformedSuppliers.slice(0, 3));
          return { suppliers: transformedSuppliers };
        } else {
          console.log('[Supplier Search] UAT API failed with status:', response.status);
          console.log('[Supplier Search] Response text:', await response.text());
        }
      } catch (error) {
        console.log('[Supplier Search] UAT API error:', error.message);
        console.log('[Supplier Search] Error details:', error);
      }

      // Fallback to comprehensive mock data
      console.log('[Supplier Search] Using comprehensive mock data fallback');
      
      const mockSuppliers = [
        {
          id: 'supplier-1',
          name: 'Pacific Gas & Electric',
          supplierName: 'PG&E',
          shortName: 'PG&E',
          logo: 'https://via.placeholder.com/40x40/3b82f6/ffffff?text=PG&E'
        },
        {
          id: 'supplier-2', 
          name: 'Southern California Edison',
          supplierName: 'SCE',
          shortName: 'SCE',
          logo: 'https://via.placeholder.com/40x40/10b981/ffffff?text=SCE'
        },
        {
          id: 'supplier-3',
          name: 'ConEdison',
          supplierName: 'ConEd',
          shortName: 'ConEd',
          logo: 'https://via.placeholder.com/40x40/f59e0b/ffffff?text=CE'
        },
        {
          id: 'supplier-4',
          name: 'National Grid',
          supplierName: 'National Grid',
          shortName: 'NG',
          logo: 'https://via.placeholder.com/40x40/8b5cf6/ffffff?text=NG'
        },
        {
          id: 'supplier-5',
          name: 'Duke Energy',
          supplierName: 'Duke Energy',
          shortName: 'Duke',
          logo: 'https://via.placeholder.com/40x40/ef4444/ffffff?text=DE'
        },
        {
          id: 'supplier-6',
          name: 'Dominion Energy',
          supplierName: 'Dominion',
          shortName: 'DE',
          logo: 'https://via.placeholder.com/40x40/06b6d4/ffffff?text=DE'
        },
        {
          id: 'supplier-7',
          name: 'Exelon Corporation',
          supplierName: 'Exelon',
          shortName: 'EXC',
          logo: 'https://via.placeholder.com/40x40/84cc16/ffffff?text=EXC'
        },
        {
          id: 'supplier-8',
          name: 'American Electric Power',
          supplierName: 'AEP',
          shortName: 'AEP',
          logo: 'https://via.placeholder.com/40x40/f97316/ffffff?text=AEP'
        },
        {
          id: 'supplier-9',
          name: 'FirstEnergy Corp',
          supplierName: 'FirstEnergy',
          shortName: 'FE',
          logo: 'https://via.placeholder.com/40x40/ec4899/ffffff?text=FE'
        },
        {
          id: 'supplier-10',
          name: 'Xcel Energy',
          supplierName: 'Xcel',
          shortName: 'XEL',
          logo: 'https://via.placeholder.com/40x40/14b8a6/ffffff?text=XEL'
        },
        {
          id: 'supplier-11',
          name: 'NextEra Energy',
          supplierName: 'NextEra',
          shortName: 'NEE',
          logo: 'https://via.placeholder.com/40x40/6366f1/ffffff?text=NEE'
        },
        {
          id: 'supplier-12',
          name: 'CenterPoint Energy',
          supplierName: 'CenterPoint',
          shortName: 'CNP',
          logo: 'https://via.placeholder.com/40x40/dc2626/ffffff?text=CNP'
        }
      ];

      console.log('[Supplier Search] Mock suppliers loaded:', mockSuppliers.length);
      return { suppliers: mockSuppliers };
    },
    enabled: true, // Always load suppliers
    staleTime: 10 * 60 * 1000, // Cache for 10 minutes
    gcTime: 30 * 60 * 1000, // Keep in cache for 30 minutes
  });
}
